/**
 * Разбор счёта на оплату, выгруженного из 1С в Excel (печатная форма), — чистая
 * функция: одни правила на сайте (предпросмотр) и на сервере (запись).
 * Паспорт — docs/features/unf-invoice-upload.md. Образцы: счета СПМ и ДИСТИНКТ
 * 2026 года; ячейки ищутся по подписям, а не по адресам — у форм разная ширина.
 */

export interface UnfInvoiceLine {
  /** Номер строки в счёте. */
  no: number
  /** «Товар (Услуга)»: «Наименование» + пробел + партномер. */
  name: string
  manufacturer: string
  quantity: number
  unit: string
  price: number
  sum: number
}

export interface UnfInvoiceVat {
  rate: number
  amount: number
  /** «В том числе НДС» — налог внутри цены; иначе — сверху («Сумма НДС»). */
  included: boolean
}

export interface ParsedUnfInvoice {
  /** Номер счёта, как напечатан: «20». */
  number: string
  /** Дата счёта `YYYY-MM-DD`. */
  date: string | null
  sellerName: string
  sellerInn: string
  buyerName: string
  buyerInn: string
  lines: UnfInvoiceLine[]
  /** «Итого». */
  total: number | null
  /** «Всего к оплате» — сколько платит клиент. */
  payable: number | null
  vat: UnfInvoiceVat[]
  /** НДС сверху: «Всего к оплате» больше «Итого» — цены в строках без налога. */
  vatOnTop: boolean
}

export class UnfInvoiceFormatError extends Error {}

type Cell = string | number | boolean | Date | null | undefined
type Row = Cell[]

const text = (v: Cell): string =>
  v == null
    ? ''
    : String(v)
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const round2 = (v: number): number => Math.round(v * 100) / 100

function toNumber(v: Cell): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const cleaned = text(v).replace(/\s/g, '').replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null
  return Number(cleaned)
}

const MONTHS_RU: Record<string, string> = {
  января: '01',
  февраля: '02',
  марта: '03',
  апреля: '04',
  мая: '05',
  июня: '06',
  июля: '07',
  августа: '08',
  сентября: '09',
  октября: '10',
  ноября: '11',
  декабря: '12',
}

/** «Счет на оплату № 20 от 13 октября 2026 г.» → номер и дата. */
export function parseUnfInvoiceTitle(
  title: string,
): { number: string; date: string | null } | null {
  const m = text(title).match(
    /^сч[её]т на оплату\s*№\s*(\S+)\s+от\s+(\d{1,2})\s+([а-яё]+)\s+(\d{4})/i,
  )
  if (!m) return null
  const month = MONTHS_RU[(m[3] ?? '').toLowerCase()]
  return {
    number: m[1] ?? '',
    date: month ? `${m[4]}-${month}-${(m[2] ?? '').padStart(2, '0')}` : null,
  }
}

/** « ООО "ДИСТИНКТ",  ИНН 7814837898,  КПП …» → название и ИНН. */
function parseParty(raw: string): { name: string; inn: string } {
  const s = text(raw)
  const inn = s.match(/ИНН\s*(\d{10,12})/i)?.[1] ?? ''
  const name = s.split(/,\s*ИНН/i)[0]?.trim() ?? ''
  return { name, inn }
}

/** Текст справа от подписи в той же строке (первая непустая ячейка после неё). */
function valueAfter(row: Row, labelIdx: number): Cell {
  for (let i = labelIdx + 1; i < row.length; i++) if (text(row[i]) !== '') return row[i]
  return null
}

function findLabel(rows: Row[], test: (s: string) => boolean): { row: Row; idx: number } | null {
  for (const row of rows) {
    const idx = row.findIndex((c) => test(text(c)))
    if (idx >= 0) return { row, idx }
  }
  return null
}

export function parseUnfInvoice(rows: Row[]): ParsedUnfInvoice {
  const titleHit = findLabel(rows, (s) => /^сч[её]т на оплату\s*№/i.test(s))
  const title = titleHit ? parseUnfInvoiceTitle(text(titleHit.row[titleHit.idx])) : null
  const isHead = (row: Row) => {
    const cells = row.map(text)
    return (
      cells.includes('№') && cells.some((c) => c.startsWith('Товар')) && cells.includes('Сумма')
    )
  }
  const headIdx = rows.findIndex(isHead)
  if (!title || headIdx < 0) {
    throw new UnfInvoiceFormatError(
      'Это не счёт на оплату из 1С: в файле должны быть заголовок «Счет на оплату № … от …» и таблица с колонками «№», «Товар (Услуга)», «Кол-во», «Цена», «Сумма»',
    )
  }

  const head = (rows[headIdx] ?? []).map(text)
  const col = (test: (s: string) => boolean) => head.findIndex(test)
  const cNo = col((s) => s === '№')
  const cName = col((s) => s.startsWith('Товар'))
  const cMan = col((s) => s.replace(/[-\s]/g, '').toLowerCase().startsWith('производи'))
  const cQty = col((s) => s.startsWith('Кол'))
  const cUnit = col((s) => s.startsWith('Ед'))
  const cPrice = col((s) => s === 'Цена')
  const cSum = col((s) => s === 'Сумма')
  if (cQty < 0 || cPrice < 0) {
    throw new UnfInvoiceFormatError('В таблице счёта не найдены колонки «Кол-во» и «Цена»')
  }

  const lines: UnfInvoiceLine[] = []
  for (const row of rows.slice(headIdx + 1)) {
    // На второй странице шапка таблицы повторяется; промежуточные итоги — без номера.
    if (isHead(row)) continue
    if (row.some((c) => /^Итого:?$/i.test(text(c)))) break
    const no = toNumber(row[cNo])
    const name = text(row[cName])
    const quantity = toNumber(row[cQty])
    const price = toNumber(row[cPrice])
    if (no == null || !Number.isInteger(no) || !name || quantity == null || price == null) continue
    lines.push({
      no,
      name,
      manufacturer: cMan >= 0 ? text(row[cMan]) : '',
      quantity,
      unit: cUnit >= 0 ? text(row[cUnit]) : '',
      price,
      sum: round2(toNumber(row[cSum]) ?? price * quantity),
    })
  }
  if (lines.length === 0) throw new UnfInvoiceFormatError('В счёте нет ни одной позиции')

  const amountOf = (test: (s: string) => boolean): number | null => {
    const hit = findLabel(rows, test)
    return hit ? toNumber(valueAfter(hit.row, hit.idx)) : null
  }
  const total = amountOf((s) => /^Итого:?$/i.test(s))
  const payable = amountOf((s) => /^Всего к оплате:?$/i.test(s))
  const vat: UnfInvoiceVat[] = []
  for (const row of rows) {
    row.forEach((c, idx) => {
      const m = text(c).match(/^(В том числе НДС|Сумма НДС)\s*(\d+(?:[.,]\d+)?)\s*%:?$/i)
      const amount = m ? toNumber(valueAfter(row, idx)) : null
      if (m && amount != null) {
        vat.push({
          rate: Number((m[2] ?? '0').replace(',', '.')),
          amount,
          included: /в том числе/i.test(m[1] ?? ''),
        })
      }
    })
  }

  const seller = findLabel(rows, (s) => s === 'Поставщик')
  const buyer = findLabel(rows, (s) => s === 'Покупатель')
  const sellerParty = parseParty(seller ? text(valueAfter(seller.row, seller.idx)) : '')
  const buyerParty = parseParty(buyer ? text(valueAfter(buyer.row, buyer.idx)) : '')

  return {
    number: title.number,
    date: title.date,
    sellerName: sellerParty.name,
    sellerInn: sellerParty.inn,
    buyerName: buyerParty.name,
    buyerInn: buyerParty.inn,
    lines,
    total,
    payable,
    vat,
    vatOnTop: total != null && payable != null && payable - total > 0.01,
  }
}
